/**
 * The bridge between the deterministic sim and the screen — *core decides,
 * Phaser renders*.
 *
 * Each fixed 60 Hz step the director:
 *  1. advances the authoritative {@link WorldState} via {@link stepWorld}
 *     (passing the player's pointer target for that step), then
 *  2. reconciles the live Phaser sprites against `world.hazards` BY ID —
 *     creating a leased sprite set the first time a hazard id appears, updating
 *     position/rotation/scale/tint to match the descriptor, and releasing the
 *     sprites once the id leaves `world.hazards` (the hazard expired in core).
 *
 * It draws NOTHING by guesswork: every coordinate, angle and lifetime comes from
 * core, so the picture on screen is exactly the world the server will re-simulate
 * for anti-cheat. The director holds no game rules — collision, motion and
 * spawning all live in `core/world.ts`.
 *
 * Telegraph vs active: a hazard is *telegraphing* (harmless preview) for
 * `telegraphMs` after `bornMs`, then *active* (lethal) for `activeMs`. During the
 * telegraph the sprite is drawn dim + tinted (a teal "incoming" warning that
 * fades/pulses in); once active it snaps to full opacity and its solid look. This
 * mirrors the legacy fly-in/charge telegraphs (AVOID_LEGACY_ANALYSIS.md §1.3).
 */
import Phaser from 'phaser';
import type { Hazard, WorldState, PlayerInput, SegmentHazard } from '../core';
import { stepWorld, SIM, WALL, segmentBlinkOn } from '../core';
import { HazardPool } from './HazardPool';
import { Explosion } from './Explosion';
import { PlayerDeathFx } from './PlayerDeathFx';
import { TurretFx } from './TurretFx';
import { LaserFx } from './LaserFx';
import { WallFx } from './WallFx';
import { WallBodyFx } from './WallBodyFx';
import { WallSpikeFx } from './WallSpikeFx';
import { BombFx } from './BombFx';
import { MissileFx } from './MissileFx';
import { CaroFx } from './CaroFx';
import { HAZARD_TEX } from './textures';

/** The author-time pixel size of the beam strip texture (matches textures.ts). */
const BEAM_TEX_W = 16;
const BEAM_TEX_H = 64;
/** The author-time pixel size of the wall bar texture. */
const WALL_TEX_W = 64;
const WALL_TEX_H = 64;
/** The author-time pixel size of the missile body texture. */
const MISSILE_TEX = 64;

/** Telegraph tint (teal "incoming" warning) applied during the preview window. */
const TELEGRAPH_TINT = 0x2ec4b6;
/** Telegraph opacity floor; the warning pulses up toward this as it approaches active. */
const TELEGRAPH_MIN_ALPHA = 0.18;
const TELEGRAPH_MAX_ALPHA = 0.55;

/** A hazard's leased render objects, kept for the hazard's lifetime. */
interface Lease {
  hazard: Hazard;
  sprites: Phaser.GameObjects.Image[];
}

export class PatternDirector {
  private readonly pool: HazardPool;
  /** Bomb detonation FX — fired once when a `bombs` hazard goes from fly-in→blast. */
  private readonly explosion: Explosion;
  /** Player death burst — the dramatic explosion when the ball is hit. */
  private readonly playerDeathFx: PlayerDeathFx;
  /** Turret laser FX — the track → charge → fire choreography (family `turret`). */
  private readonly turretFx: TurretFx;
  /** Laser-wall / sweep FX — turret-grade beam + crackle particles (family `lasers`). */
  private readonly laserFx: LaserFx;
  /** Wall-crush FX — debris + flash + shake when a crusher closes (family `walls`). */
  private readonly wallFx: WallFx;
  /** Wall body FX — the tiled armored plating of each crusher wall (family `walls`). */
  private readonly wallBodyFx: WallBodyFx;
  /** Wall spike FX — the glowing teeth on each crusher's inner face (family `walls`). */
  private readonly wallSpikeFx: WallSpikeFx;
  /** Bomb target FX — the spinning lock-on reticle on the landing point (family `bombs`). */
  private readonly bombFx: BombFx;
  /** Missile exhaust FX — the teal/gold trail behind flying missiles (family `missiles`). */
  private readonly missileFx: MissileFx;
  /** Caro core FX — the pulsing energy hub at the rotating carousel's centre (family `caro`). */
  private readonly caroFx: CaroFx;
  /** Live leases keyed by hazard id — the source of truth for reconciliation. */
  private readonly leases = new Map<number, Lease>();
  /** Scratch set reused each step to detect hazards that have expired in core. */
  private readonly seen = new Set<number>();
  /**
   * Bomb ids that have already detonated this run. A `bombs` hazard is harmless
   * during its fly-in (telegraph) then explodes at the landing point; we paint the
   * blast exactly once, on the frame its phase first reaches active. Tracking by id
   * makes the detection edge-triggered (and replay-safe — same path every run).
   */
  private readonly detonated = new Set<number>();
  /** Wall ids that have already slammed (edge-triggered crush FX, once per wall). */
  private readonly crushed = new Set<number>();

  constructor(scene: Phaser.Scene) {
    this.pool = new HazardPool(scene);
    this.explosion = new Explosion(scene);
    this.playerDeathFx = new PlayerDeathFx(scene);
    this.turretFx = new TurretFx(scene);
    this.laserFx = new LaserFx(scene);
    this.wallFx = new WallFx(scene);
    this.wallBodyFx = new WallBodyFx(scene);
    this.wallSpikeFx = new WallSpikeFx(scene);
    this.bombFx = new BombFx(scene);
    this.missileFx = new MissileFx(scene);
    this.caroFx = new CaroFx(scene);
  }

  /**
   * Advance the sim by one fixed step and re-sync the visuals to it.
   *
   * @param world the authoritative world (mutated in place by {@link stepWorld}).
   * @param input the player's pointer target for this step (viewport px).
   */
  step(world: WorldState, input: PlayerInput): void {
    stepWorld(world, SIM.fixedDeltaMs, input);
    this.sync(world);
  }

  /**
   * Fire the dramatic player-death burst at `(x,y)` using the skin's colours
   * (`body`/`highlight`/`rim`) and ball radius `r`. Called once when the run ends; the
   * scene then plays the in-flight attacks out before the game-over screen.
   */
  explodePlayer(x: number, y: number, r: number, body: number, highlight: number, rim: number): void {
    this.playerDeathFx.blast(x, y, r, body, highlight, rim);
  }

  /**
   * Reconcile sprites against `world.hazards` without advancing the sim. Useful
   * for an initial paint, or to redraw after a resize. Idempotent.
   */
  sync(world: WorldState): void {
    this.seen.clear();

    for (const hazard of world.hazards) {
      this.seen.add(hazard.id);
      // Turret lasers get the dedicated track → charge → fire FX (reticle + charge
      // crackle + muzzle flash); they don't use the plain pooled beam lease.
      if (hazard.kind === 'segment' && hazard.family === 'turret') {
        this.turretFx.update(hazard, world.elapsedMs, world.playerX, world.playerY, world.viewport);
        continue;
      }
      // Bombs have NO projectile body: the BombFx reticle flies into place and the
      // Explosion takes over on detonation. Delegate fully (no pooled lease).
      if (hazard.kind === 'circleMover' && hazard.family === 'bombs') {
        this.maybeDetonate(hazard, world.elapsedMs);
        this.bombFx.update(hazard, world.elapsedMs);
        continue;
      }
      // Laser walls / sweeps get the turret-grade beam FX (glow + hot core + endpoint
      // flares + crackle particles) instead of the plain pooled rectangle.
      if (hazard.kind === 'segment' && hazard.family === 'lasers') {
        this.laserFx.update(hazard, world.elapsedMs);
        continue;
      }
      // Walls: a TILED armored body + spiked crushing face + the crush burst. Delegated
      // (no stretched pooled sprite — the body is a TileSprite for crisp detail).
      if (hazard.kind === 'rect' && hazard.family === 'walls') {
        const ph = this.phase(hazard, world.elapsedMs);
        this.maybeCrush(hazard, world);
        this.wallBodyFx.update(hazard, ph);
        this.wallSpikeFx.update(hazard, world.viewport, world.elapsedMs, ph);
        continue;
      }
      let lease = this.leases.get(hazard.id);
      if (!lease) {
        const count = this.pool.spriteCountFor(hazard);
        lease = { hazard, sprites: this.pool.acquireFor(hazard, count) };
        this.leases.set(hazard.id, lease);
      } else {
        // Keep the latest descriptor (its mutable fields advanced in core).
        lease.hazard = hazard;
      }
      this.draw(hazard, lease.sprites, world.elapsedMs);
      // Missiles get an exhaust trail; the caro a glowing energy core + barrels at its hub.
      if (hazard.kind === 'circleMover') {
        if (hazard.family === 'missiles') this.missileFx.emit(hazard);
      } else if (hazard.kind === 'rotatingSegment') {
        const active = world.elapsedMs >= hazard.bornMs + hazard.telegraphMs;
        this.caroFx.update(
          hazard.id,
          hazard.cx,
          hazard.cy,
          hazard.length,
          hazard.angle0,
          hazard.beams,
          world.elapsedMs,
          active,
        );
      }
    }

    // Release any hazard that core dropped this step (it expired / was recycled).
    for (const [id, lease] of this.leases) {
      if (this.seen.has(id)) continue;
      this.pool.releaseFor(lease.hazard, lease.sprites);
      this.leases.delete(id);
      // Drop any detonation / crush marker so a recycled id can fire again.
      this.detonated.delete(id);
      this.crushed.delete(id);
    }
    // Release the FX for any turret / laser / bomb / wall that expired this step.
    this.turretFx.sweep(this.seen);
    this.laserFx.sweep(this.seen);
    this.bombFx.sweep(this.seen);
    this.caroFx.sweep(this.seen);
    this.wallBodyFx.sweep(this.seen);
    this.wallSpikeFx.sweep(this.seen);
  }

  /**
   * Fire the explosion FX once, the first time a `bombs`-family hazard crosses
   * from its fly-in (telegraph) into its lethal blast (active) phase. The bomb's
   * `to` is the landing point and `r` the blast radius, so the FX footprint
   * matches the lethal circle core owns. Edge-triggered per id via {@link detonated}.
   */
  private maybeDetonate(hazard: Hazard, elapsedMs: number): void {
    if (hazard.family !== 'bombs' || hazard.kind !== 'circleMover') return;
    if (this.detonated.has(hazard.id)) return;
    // Active begins exactly at bornMs + telegraphMs (see core/world isActive).
    if (elapsedMs < hazard.bornMs + hazard.telegraphMs) return;
    this.detonated.add(hazard.id);
    this.explosion.blast(hazard.to.x, hazard.to.y, hazard.r);
  }

  /**
   * Fire the wall-crush FX once, the first time a `walls`-family crusher closes
   * (telegraph → active). The burst runs down the wall's INNER seam — the edge
   * facing the play area — flung toward the open, safe side. Edge-triggered per id.
   */
  private maybeCrush(hazard: Hazard, world: WorldState): void {
    if (hazard.family !== 'walls' || hazard.kind !== 'rect') return;
    if (this.crushed.has(hazard.id)) return;
    // Fire when the plate has actually SLAMMED to its closed seam — i.e. after the
    // fast crush completes (telegraph + crushFraction of the active window) — not at
    // active-start when it's still creeping, so the burst lands on the closed wall.
    if (world.elapsedMs < hazard.bornMs + hazard.telegraphMs + hazard.activeMs * WALL.crushFraction) {
      return;
    }
    this.crushed.add(hazard.id);
    // Burst along the wall's INNER seam (the edge facing the play area) at its closed
    // position, flung toward the open half. Orientation follows the crush axis.
    if (hazard.crushAxis === 'y') {
      const closedY = hazard.crushPos ?? hazard.y;
      const onTop = closedY + hazard.h / 2 < world.viewport.height / 2;
      const seamY = onTop ? closedY + hazard.h : closedY;
      this.wallFx.crush(0, seamY, false, hazard.w, onTop ? 90 : 270);
    } else {
      const closedX = hazard.crushPos ?? hazard.x;
      const onLeft = closedX + hazard.w / 2 < world.viewport.width / 2;
      const seamX = onLeft ? closedX + hazard.w : closedX;
      this.wallFx.crush(seamX, 0, true, hazard.h, onLeft ? 0 : 180);
    }
  }

  /** Reclaim every sprite (on `scene.restart()`); the next `sync` repopulates. */
  reset(): void {
    for (const lease of this.leases.values()) {
      this.pool.releaseFor(lease.hazard, lease.sprites);
    }
    this.leases.clear();
    this.seen.clear();
    this.detonated.clear();
    this.crushed.clear();
    this.explosion.reset();
    this.playerDeathFx.reset();
    this.turretFx.reset();
    this.laserFx.reset();
    this.wallFx.reset();
    this.wallBodyFx.reset();
    this.wallSpikeFx.reset();
    this.bombFx.reset();
    this.missileFx.reset();
    this.caroFx.reset();
  }

  /** Tear down all render objects (scene shutdown). */
  destroy(): void {
    this.leases.clear();
    this.seen.clear();
    this.detonated.clear();
    this.crushed.clear();
    this.explosion.destroy();
    this.playerDeathFx.destroy();
    this.turretFx.destroy();
    this.laserFx.destroy();
    this.wallFx.destroy();
    this.wallBodyFx.destroy();
    this.wallSpikeFx.destroy();
    this.bombFx.destroy();
    this.missileFx.destroy();
    this.caroFx.destroy();
    this.pool.destroy();
  }

  /* --------------------------------------------------------------------- *
   * Per-kind drawing. Each maps a Hazard descriptor onto its leased
   * sprite(s) and applies the telegraph-vs-active visual.
   * --------------------------------------------------------------------- */

  private draw(hazard: Hazard, sprites: Phaser.GameObjects.Image[], elapsedMs: number): void {
    switch (hazard.kind) {
      case 'rect':
        this.drawRect(hazard, sprites[0], elapsedMs);
        break;
      case 'segment':
        this.drawSegment(
          sprites[0],
          hazard.x1,
          hazard.y1,
          hazard.x2,
          hazard.y2,
          hazard.thickness,
          this.phase(hazard, elapsedMs),
        );
        // A blinking sweep laser dims to a faint primed line on its OFF beats so
        // the player reads exactly when it's safe to dash across.
        if (hazard.blinkPeriodMs !== undefined) {
          this.applyBlink(sprites[0], hazard, elapsedMs);
        }
        break;
      case 'rotatingSegment':
        this.drawRotating(hazard, sprites, elapsedMs);
        break;
      case 'circleMover':
        this.drawCircleMover(hazard, sprites[0], elapsedMs);
        break;
    }
  }

  /** Axis-aligned wall bar: stretch the wall texture to `w`×`h` at its centre. */
  private drawRect(
    hazard: Extract<Hazard, { kind: 'rect' }>,
    sprite: Phaser.GameObjects.Image,
    elapsedMs: number,
  ): void {
    sprite.setOrigin(0, 0); // hazard x,y is the top-left
    sprite.setPosition(hazard.x, hazard.y);
    sprite.setAngle(0);
    sprite.setScale(hazard.w / WALL_TEX_W, hazard.h / WALL_TEX_H);
    sprite.setTexture(HAZARD_TEX.wall);
    this.applyPhase(sprite, this.phase(hazard, elapsedMs));
  }

  /** One straight beam between two points, drawn with the beam strip texture. */
  private drawSegment(
    sprite: Phaser.GameObjects.Image,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    thickness: number,
    phase: number,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.hypot(dx, dy);
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    sprite.setOrigin(0.5, 0.5);
    sprite.setTexture(HAZARD_TEX.beam);
    sprite.setPosition(midX, midY);
    // The beam strip is authored vertical (length along +y); rotate so its long
    // axis lies along the segment. atan2 + 90° aligns the texture's y-axis.
    sprite.setRotation(Math.atan2(dy, dx) + Math.PI / 2);
    sprite.setScale(thickness / BEAM_TEX_W, length / BEAM_TEX_H);
    this.applyPhase(sprite, phase);
  }

  /** The caro: `beams` strips of length `length` evenly spaced about a centre. */
  private drawRotating(
    hazard: Extract<Hazard, { kind: 'rotatingSegment' }>,
    sprites: Phaser.GameObjects.Image[],
    elapsedMs: number,
  ): void {
    const phase = this.phase(hazard, elapsedMs);
    const step = (Math.PI * 2) / hazard.beams;
    for (let i = 0; i < sprites.length; i++) {
      const angle = hazard.angle0 + i * step;
      // Beam runs from the centre outward to `length`.
      const ex = hazard.cx + Math.cos(angle) * hazard.length;
      const ey = hazard.cy + Math.sin(angle) * hazard.length;
      this.drawSegment(sprites[i], hazard.cx, hazard.cy, ex, ey, hazard.thickness, phase);
    }
  }

  /** Missile body: place at the live centre, scale to `2r`, nose leading travel. */
  private drawCircleMover(
    hazard: Extract<Hazard, { kind: 'circleMover' }>,
    sprite: Phaser.GameObjects.Image,
    elapsedMs: number,
  ): void {
    // Only missiles reach here — bombs are bodyless (delegated to BombFx in `sync`).
    sprite.setOrigin(0.5, 0.5);
    sprite.setPosition(hazard.x, hazard.y);
    sprite.setTexture(HAZARD_TEX.missile);
    sprite.setScale((hazard.r * 2) / MISSILE_TEX);
    // Missile art points UP (-y); rotate so the nose leads its travel direction.
    const dx = hazard.to.x - hazard.from.x;
    const dy = hazard.to.y - hazard.from.y;
    sprite.setRotation(Math.atan2(dy, dx) + Math.PI / 2);
    this.applyPhase(sprite, this.phase(hazard, elapsedMs));
  }

  /* --------------------------------------------------------------------- *
   * Telegraph / active visual.
   * --------------------------------------------------------------------- */

  /**
   * Where a hazard is in its lifecycle:
   *  - `< telegraphMs` from birth → telegraph progress in [0,1) (preview),
   *  - otherwise → active (`1`, lethal/solid).
   * Returns the telegraph progress, or 1 once active.
   */
  private phase(hazard: Hazard, elapsedMs: number): number {
    const age = elapsedMs - hazard.bornMs;
    if (hazard.telegraphMs <= 0) return 1;
    if (age >= hazard.telegraphMs) return 1;
    return Phaser.Math.Clamp(age / hazard.telegraphMs, 0, 1);
  }

  /**
   * Apply the telegraph-vs-active look to a sprite. `phase` < 1 means
   * telegraphing: dim + teal-tinted, ramping toward solid as it nears active.
   * `phase` >= 1 means active: full opacity, no tint (the solid hazard art).
   */
  private applyPhase(sprite: Phaser.GameObjects.Image, phase: number): void {
    if (phase >= 1) {
      sprite.clearTint();
      sprite.setAlpha(1);
      return;
    }
    sprite.setTint(TELEGRAPH_TINT);
    sprite.setAlpha(
      TELEGRAPH_MIN_ALPHA + (TELEGRAPH_MAX_ALPHA - TELEGRAPH_MIN_ALPHA) * phase,
    );
  }

  /**
   * Blink overlay for a sweeping laser. During its active window the beam is lethal
   * only on the ON beats; on the OFF beats core makes it harmless, so we drop it to a
   * faint primed line (teal, low alpha) — the visual cue that it's safe to cross.
   * Leaves the telegraph look untouched (that's `applyPhase`'s job).
   */
  private applyBlink(
    sprite: Phaser.GameObjects.Image,
    hazard: SegmentHazard,
    elapsedMs: number,
  ): void {
    if (elapsedMs < hazard.bornMs + hazard.telegraphMs) return; // still telegraphing
    if (segmentBlinkOn(hazard, elapsedMs)) {
      sprite.clearTint();
      sprite.setAlpha(1);
    } else {
      sprite.setTint(TELEGRAPH_TINT);
      sprite.setAlpha(0.14);
    }
  }
}
